import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withBreakpoints } from 'react-breakpoints';
import pluralize from 'pluralize';

import SearchDropdown from '@components/common/search_dropdown';
import DropdownToggleButton from '@components/common/search_dropdown/dropdown_toggle_button';

import { keyPressHelper } from '@helpers/accessibility';
import { filterMatch } from '@helpers/search_filters';
import { sortStrings } from '@helpers/sort_helpers';

import fetchOrgs from '@api/v4/organizations';

// flatten out the nested org -> folder object
const collateOrgsAndFolders = (organizations) => organizations.sort(sortStrings).map((org) => (
  {
    ...org,
    brandfolders: org.brandfolders.sort(sortStrings),
    brandguides: org.brandguides.sort(sortStrings),
    portals: org.portals.sort(sortStrings),
  }
));

const searchOrgsAndFolders = (organizations, filter) => organizations.reduce((acc, org) => {
  if (filter === '') {
    return acc.concat([org, ...org.brandfolders, ...org.brandguides, ...org.portals]);
  }
  const folders = [[], org.brandfolders, org.brandguides, org.portals].reduce((prev, subType) => ([
    ...prev,
    ...subType.filter((i) => filterMatch(i.name, filter))
  ]));
  if (!folders.length) {
    // We want to still include the org name in the search results,
    // even if it doesn't have any brandfolders that match!
    if (filterMatch(org.name, filter)) {
      return acc.concat([org]);
    }
    return acc;
  }
  return acc.concat([org, ...folders]);
}, []);

const NavigationDropdownWrapper = (props) => {
  const [organizations, setOrganizations] = useState([]);
  const [menuVisible, setMenuVisibility] = useState(false);
  const whitelabelingLibraryName = pluralize(props.libraryName);

  useEffect(() => {
    fetchOrgs().then((orgs) => setOrganizations(collateOrgsAndFolders(orgs)));
  // can we rely on rails views to update this component? Is there another prop we can rely on?
  }, []);

  const isMobile = props.currentBreakpoint === 'mobile';

  return (
    <>
      { isMobile ? (
        <>
          <div
            onClick={() => setMenuVisibility(!menuVisible)}
            onKeyPress={keyPressHelper(() => setMenuVisibility(!menuVisible))}
            className="navigation-dropdown-wrapper"
            data-testid="navigation-dropdown-wrapper"
            role="button"
            tabIndex="0"
          >
            <DropdownToggleButton title={`${t('My')} ${t(whitelabelingLibraryName)}`} />
          </div>
          {
            menuVisible
            && (
              <SearchDropdown
                searchIndex={organizations}
                searchIndexTitle={whitelabelingLibraryName}
                searchFunc={searchOrgsAndFolders}
                maxResults={9999999}
              />
            )
          }
        </>
      ) : (
        <div
          onMouseEnter={() => setMenuVisibility(true)}
          onMouseLeave={() => setMenuVisibility(false)}
          className="navigation-dropdown-wrapper"
          data-testid="navigation-dropdown-wrapper"
        >
          <a href="/organizations">
            <DropdownToggleButton
              ariaLabel={`${t('Searchable list of your')} ${whitelabelingLibraryName}`}
              onToggle={() => setMenuVisibility((prev) => !prev)}
              title={`${t('My')} ${t(whitelabelingLibraryName)}`}
            />
          </a>
          {
            menuVisible
            && (
              <SearchDropdown
                searchIndex={organizations}
                searchIndexTitle={whitelabelingLibraryName}
                searchFunc={searchOrgsAndFolders}
                maxResults={9999999}
              />
            )
          }
        </div>
      )}
    </>
  );
};

NavigationDropdownWrapper.defaultProps = {
  libraryName: "Brandfolder",
  currentBreakpoint: 'desktop'
};

NavigationDropdownWrapper.propTypes = {
  libraryName: PropTypes.string,
  currentBreakpoint: PropTypes.string
};

export default withBreakpoints(NavigationDropdownWrapper);
